import React, { useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import "./CRMModule.css";
import apiClient from "../../../../api/apiClient";
import Loading from "../../../../components/Loading/Loading";
import CRMDashboard from "./CRMDashboard";
import CRMReports from "./CRMReports";
import LeadList from "./LeadList";
import LeadDetail from "./LeadDetail";
import PipelineBoard from "./PipelineBoard";
import CampaignTab from "./campaign/CampaignTab";
import ConversationsTab from "./conversations/ConversationsTab";
import CompaniesTab from "./CompaniesTab";
import CompanyDetail from "./CompanyDetail";
import TabLockScreen from "./TabLockScreen";

const TABS = [
    { key: "dashboard", label: "Dashboard" },
    { key: "leads",     label: "Leads" },
    { key: "pipeline",  label: "Pipeline" },
    { key: "companies", label: "Companies" },
    // { key: "campaign",       label: "Campaign" },
    // { key: "conversations",  label: "Conversations" },
    { key: "reports",   label: "Reports" },
];

const CRMModule = ({ userProfile, userId }) => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(() => {
        const requested = sessionStorage.getItem("crmOpenTab");
        console.log("[CRMModule] useState init, sessionStorage crmOpenTab =", requested);
        if (requested) {
            sessionStorage.removeItem("crmOpenTab");
            return requested;
        }
        return "dashboard";
    });
    const [stages, setStages] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [bootstrapping, setBootstrapping] = useState(true);
    const [selectedLeadId, setSelectedLeadId] = useState(null);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [unreadConvCount, setUnreadConvCount] = useState(0);

    // Pick up sessionStorage signal on every navigation (handles already-mounted case)
    useEffect(() => {
        const requested = sessionStorage.getItem("crmOpenTab");
        console.log("[CRMModule] location.key effect fired, sessionStorage crmOpenTab =", requested, "location.key =", location.key);
        if (requested) {
            sessionStorage.removeItem("crmOpenTab");
            setActiveTab(requested);
        }
    }, [location.key]);

    useEffect(() => {
        Promise.allSettled([
            apiClient.get("/admin/crm/pipeline-stages"),
            apiClient.get("/admin/users?adminOnly=true"),
        ]).then(([stagesResult, adminsResult]) => {
            if (stagesResult.status === "fulfilled") setStages(stagesResult.value.data.stages || []);
            else console.error("Failed to load pipeline stages:", stagesResult.reason);
            if (adminsResult.status === "fulfilled") setAdmins(adminsResult.value.data || []);
            else console.error("Failed to load admins:", adminsResult.reason);
        }).finally(() => setBootstrapping(false));
    }, []);

    useEffect(() => {
        const fetchUnread = () => {
            apiClient.get("/admin/crm/conversations/unread-count")
                .then((res) => setUnreadConvCount(res.data.count || 0))
                .catch(() => {});
        };
        fetchUnread();
        const interval = setInterval(fetchUnread, 30_000);
        return () => clearInterval(interval);
    }, []);

    const handleSelectLead = (leadId) => {
        setSelectedLeadId(leadId);
    };

    const handleSelectCompany = (companyId) => {
        setSelectedCompanyId(companyId);
    };

    const handleBackFromDetail = () => {
        setSelectedLeadId(null);
    };


    const handleBackFromCompany = () => {
        setSelectedCompanyId(null);
    };

    if (bootstrapping) return <div className="admin-loading-wrapper"><Loading /></div>;
    
    // If a lead is selected, show detail view regardless of sub-tab
    if (selectedLeadId) {
        return (
            <div>
                <LeadDetail
                    leadId={selectedLeadId}
                    stages={stages}
                    admins={admins}
                    onBack={handleBackFromDetail}
                    userProfile={userProfile}
                    currentUserId={userId}
                />
            </div>
        );
    }

    // If a company is selected, show company detail view
    if (selectedCompanyId) {
        return (
            <div>
                <CompanyDetail
                    companyId={selectedCompanyId}
                    onBack={handleBackFromCompany}
                    onSelectLead={(leadId) => {
                        setSelectedCompanyId(null);
                        setSelectedLeadId(leadId);
                    }}
                    currentUserId={userId}
                    userProfile={userProfile}
                />
            </div>
        );
    }

    return (
        <div>
            <div className="crm-sub-tab-bar">
                {TABS.map((tab) => (
                    <button
                        key={tab.key}
                        className={`crm-sub-tab-btn ${activeTab === tab.key ? "active" : ""}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                        {tab.key === "conversations" && unreadConvCount > 0 && (
                            <span className="crm-conv-tab-badge">
                                {unreadConvCount > 99 ? "99+" : unreadConvCount}
                            </span>
                        )}
                    </button>
                ))}
            </div>
                                   
            {activeTab === "dashboard" && <CRMDashboard />}
            {activeTab === "leads" && (
                <LeadList
                    stages={stages}
                    admins={admins}
                    onSelectLead={handleSelectLead}
                    userProfile={userProfile}
                />
            )}
            {activeTab === "pipeline" && (
                <PipelineBoard
                    stages={stages}
                    onSelectLead={handleSelectLead}
                    onSelectCompany={handleSelectCompany}
                />
            )}
            {activeTab === "companies" && <CompaniesTab onSelectCompany={handleSelectCompany} />}
            {activeTab === "campaign" && (
                <div style={{ position: "relative", minHeight: 420 }}>
                    <TabLockScreen tabLabel="Campaign">
                        <CampaignTab stages={stages} admins={admins} />
                    </TabLockScreen>
                </div>
            )}
            {activeTab === "conversations" && (
                <div style={{ position: "relative", minHeight: 420 }}>
                    <TabLockScreen tabLabel="Conversations">
                        <ConversationsTab admins={admins} />
                    </TabLockScreen>
                </div>
            )}
            {activeTab === "reports" && <CRMReports userProfile={userProfile} />}
        </div>
    );
};

export default CRMModule;
